import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { SmartShell } from '../../components/smart/SmartShell';
import { Button } from '../../components/smart/ui/Button';
import { SMART_ROUTES } from '../../constants';
import {
  smartMockQuote,
  smartMockShareLink,
} from '../../gallery/smartMock';
import {
  buildShareText,
  shareOrCopyMessage,
} from '../../utils/shareMessage';

const GALLERY_SHARE_FIRST_NAME = 'María';

export const ShareLinkDonePage = () => {
  const { t, i18n } = useTranslation('smart');
  const navigate = useNavigate();
  const link = smartMockShareLink;
  const [copied, setCopied] = useState(false);
  const variantKey = 'request';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt(t('wizard.done.share.manualPrompt'), link);
    }
  };

  const handleShare = async () => {
    const message = buildShareText(
      (key, options) => {
        if (key === 'share.shareTextWithName') {
          return t(`wizard.done.share.${variantKey}.shareTextWithName`, options);
        }
        if (key === 'share.shareTextWithoutName') {
          return t(
            `wizard.done.share.${variantKey}.shareTextWithoutName`,
            options,
          );
        }
        return t(key, options);
      },
      {
        firstName: GALLERY_SHARE_FIRST_NAME,
        amount: smartMockQuote.receiveAmount,
        currency: smartMockQuote.receiveCurrency,
        url: link,
        language: i18n.language,
      },
    );

    await shareOrCopyMessage(message, {
      title: t(`wizard.done.share.${variantKey}.shareTitle`),
      fallbackPrompt: t('wizard.done.share.manualPrompt'),
    });
  };

  return (
    <SmartShell hideHeader>
      <section className="border-border bg-bg-elevated rounded-3xl border p-6">
        <div className="text-center">
          <span
            aria-hidden
            className="bg-accent/15 text-accent mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full text-2xl"
          >
            ✓
          </span>
          <h2 className="mt-4 text-2xl font-semibold tracking-tight">
            {t(`wizard.done.share.${variantKey}.title`)}
          </h2>
          <p className="text-fg-muted mx-auto mt-2 max-w-xs text-sm">
            {t(`wizard.done.share.${variantKey}.description`)}
          </p>
        </div>

        <div className="border-border bg-bg/40 mt-5 flex items-center gap-2 rounded-xl border px-3 py-2.5 text-xs">
          <span className="text-fg flex-1 truncate font-mono">{link}</span>
          <button
            type="button"
            onClick={() => void handleCopy()}
            className="text-accent shrink-0 text-[11px] font-semibold uppercase tracking-wide"
          >
            {copied ? t('wizard.done.share.copied') : t('wizard.done.share.copy')}
          </button>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2">
          <Button variant="secondary" onClick={() => void handleCopy()}>
            {copied
              ? t('wizard.done.share.copied')
              : t('wizard.done.share.copyLink')}
          </Button>
          <Button onClick={() => void handleShare()}>
            {t('wizard.done.share.share')}
          </Button>
        </div>

        <div className="mt-3">
          <Button
            variant="secondary"
            fullWidth
            onClick={() => navigate(SMART_ROUTES.HOME)}
          >
            {t('wizard.done.share.home')}
          </Button>
        </div>
      </section>
    </SmartShell>
  );
};
